import { Loader2, X } from 'lucide-react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { FOUNDERS_CALENDLY_URL, Z_INDEX } from '../constants';
import { trackLandingEvent } from '../lib/analytics';
import { getStoredAttribution } from '../lib/attribution';
import {
  buildCalendlyIframeSrc,
  isCalendlyMessage,
  type CalendlyPrefill,
  type CalendlyScheduledPayload,
  type CalendlyUtm,
} from '../lib/calendly';

const LOAD_TIMEOUT_MS = 15_000;

export type CalendlyDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  url: string;
  prefill?: CalendlyPrefill;
  utm?: CalendlyUtm;
  onEventScheduled?: (payload: CalendlyScheduledPayload) => void;
};

type CalendlyDialogContextValue = {
  isOpen: boolean;
  open: () => void;
  close: () => void;
};

const CalendlyDialogContext = createContext<CalendlyDialogContextValue | null>(null);

export function useCalendlyDialog() {
  const ctx = useContext(CalendlyDialogContext);
  if (!ctx) {
    throw new Error('useCalendlyDialog must be used within CalendlyDialogProvider');
  }
  return ctx;
}

export function CalendlyDialog({
  isOpen,
  onClose,
  url,
  prefill,
  utm,
  onEventScheduled,
}: CalendlyDialogProps) {
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [iframeError, setIframeError] = useState(false);
  const loadTimeoutRef = useRef<number | null>(null);

  const clearLoadTimeout = useCallback(() => {
    if (loadTimeoutRef.current !== null) {
      window.clearTimeout(loadTimeoutRef.current);
      loadTimeoutRef.current = null;
    }
  }, []);

  const iframeSrc = useMemo(
    () => (isOpen ? buildCalendlyIframeSrc(url, { prefill, utm }) : null),
    [isOpen, url, prefill, utm],
  );

  useEffect(() => {
    if (!isOpen) return;

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const onMessage = (event: MessageEvent) => {
      if (!isCalendlyMessage(event)) return;

      switch (event.data.event) {
        case 'calendly.event_scheduled':
          trackLandingEvent('calendly_event_scheduled', {
            method: 'talk_to_sales',
            page_path: typeof window !== 'undefined' ? window.location.pathname : '/',
          });
          onEventScheduled?.(event.data.payload);
          onClose();
          break;
        case 'calendly.date_and_time_selected':
        case 'calendly.profile_page_viewed':
          break;
        default:
          break;
      }
    };

    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [isOpen, onClose, onEventScheduled]);

  useEffect(() => {
    if (!isOpen) {
      clearLoadTimeout();
      setIframeLoaded(false);
      setIframeError(false);
      return;
    }

    setIframeLoaded(false);
    setIframeError(false);
    clearLoadTimeout();

    loadTimeoutRef.current = window.setTimeout(() => {
      setIframeError(true);
    }, LOAD_TIMEOUT_MS);

    return clearLoadTimeout;
  }, [isOpen, iframeSrc, clearLoadTimeout]);

  const handleIframeLoad = useCallback(() => {
    clearLoadTimeout();
    setIframeLoaded(true);
    setIframeError(false);
  }, [clearLoadTimeout]);

  const handleIframeError = useCallback(() => {
    setIframeError(true);
  }, []);

  if (!isOpen || typeof document === 'undefined') return null;

  return createPortal(
    <div
      className="fixed inset-0 flex items-center justify-center sm:p-4"
      style={{ zIndex: Z_INDEX.modal }}
    >
      <div
        className="absolute inset-0 bg-black/80"
        onClick={onClose}
        aria-hidden="true"
      />

      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="calendly-dialog-title"
        onClick={(e) => e.stopPropagation()}
        className="relative z-10 flex h-dvh w-screen flex-col overflow-hidden border-0 bg-surface-lowest shadow-[0_40px_80px_-24px_rgba(0,0,0,0.85)] sm:h-[min(92vh,880px)] sm:min-h-[780px] sm:w-[min(1100px,96vw)] sm:rounded-lg sm:border sm:border-outline-variant"
      >
          <div className="flex shrink-0 items-center justify-between border-b border-outline-variant px-5 py-4 sm:px-6">
            <p
              id="calendly-dialog-title"
              className="font-mono text-[10px] tracking-[0.22em] uppercase text-primary m-0"
            >
              - Talk to Sales
            </p>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close scheduling dialog"
              className="flex h-9 w-9 items-center justify-center rounded text-on-surface-variant transition-colors hover:bg-surface-container hover:text-on-surface"
            >
              <X size={18} strokeWidth={1.75} />
            </button>
          </div>

          <div
            className="relative min-h-0 flex-1 bg-surface-lowest"
            style={{ colorScheme: 'light' }}
          >
            {!iframeLoaded && !iframeError && (
              <div className="absolute inset-0 flex items-center justify-center bg-surface-lowest">
                <Loader2 size={28} className="animate-spin text-on-surface-variant" aria-hidden="true" />
                <span className="sr-only">Loading scheduler</span>
              </div>
            )}

            {iframeError && (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-surface-lowest px-6 text-center">
                <p className="m-0 text-sm text-on-surface-variant">
                  Unable to load the scheduler. Please try again or visit Calendly directly.
                </p>
                <a
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-primary underline"
                >
                  Open in Calendly
                </a>
              </div>
            )}

            {iframeSrc && !iframeError && (
              <iframe
                key={iframeSrc}
                src={iframeSrc}
                title="Schedule a meeting"
                frameBorder={0}
                allow="payment"
                onLoad={handleIframeLoad}
                onError={handleIframeError}
                className="absolute inset-0 h-full w-full border-0 transition-opacity duration-200"
                style={{ opacity: iframeLoaded ? 1 : 0 }}
              />
            )}
          </div>
      </div>
    </div>,
    document.body,
  );
}

export function CalendlyDialogProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);

  const value = useMemo(() => ({ isOpen, open, close }), [isOpen, open, close]);

  const utm = useMemo(() => {
    const attribution = getStoredAttribution();
    return {
      source: attribution.utm_source,
      medium: attribution.utm_medium,
      campaign: attribution.utm_campaign,
    };
  }, []);

  return (
    <CalendlyDialogContext.Provider value={value}>
      {children}
      <CalendlyDialog isOpen={isOpen} onClose={close} url={FOUNDERS_CALENDLY_URL} utm={utm} />
    </CalendlyDialogContext.Provider>
  );
}
